import { SupportedEventEmitter } from "@hds/events";
import { TypeUtilities } from "@hds/common-utils";
import { JsonPatchFilteringUtilities, JsonPatchUtilities } from "@hds/json-patch-utils";
class EntityChangeFeedWatcher extends SupportedEventEmitter {
    #_watchId;
    #_watchFilter;//by-id, by-ids, by-properties, by-all
    #_entitiesMap;// Map<id,entity>
    #_specificChangeListeners = new Map();
    constructor(watchId, watchFilter, entities = new Map()) {
        super(['entity-added', 'entity-updated', 'entity-removed'])
        this.#_watchId = watchId;
        this.#_watchFilter = watchFilter;
        this.#_entitiesMap = entities;
    }
    /*****************************************************/
    /* START RETRIEVAL METHODS */
    /*****************************************************/
    get watchId() {
        return this.#_watchId;
    }
    get watchType() {
        return this.#_watchFilter?.type;
    }
    get watchFilter() {
        return this.#_watchFilter;
    }
    get entities() {
        return this.#_entitiesMap;
    }
    hasEntity(entityId) {
        return this.#_entitiesMap.has(entityId);
    }
    getEntity(entityId) {
        return this.#_entitiesMap.get(entityId);
    }
    getEntitiesByProperties(propertiesFilter) {
        const entityDataModelArray = [];
        for (const [entityId, entity] of this.entities.entries()) {
            const document = entity.toJSON();
            let match = true;
            for (const [key, val] of Object.entries(propertiesFilter)) {
                if (!document.hasOwnProperty(key) || document[key] !== val) {
                    match = false;
                    break;
                }
            }
            if (match) {
                entityDataModelArray.push(entity);
            }
        }
        return entityDataModelArray;
    }
    /*****************************************************/
    /* END RETRIEVAL METHODS */
    /* START MUTATION METHODS */
    /*****************************************************/
    async addEntity(entity, changeEvent = undefined) {
        if (entity.options?.autoSynchronization === true) {
            throw new Error('MAJOR PROBLEM someone is using a Entity that is auto syncing in a changefeed....');
        }
        this.#_entitiesMap.set(entity.id, entity);
        entity._synchronize();
        await this.triggerAndAwait('entity-added', entity, changeEvent);
    }
    async addEntities(entities, changeEvent = undefined) {
        const proms = [];
        for (const entity of entities) {
            proms.push(this.addEntity(entity));
        }
        await Promise.all(proms);
    }
    async removeEntity(entity, changeEvent = undefined) {
        await this.removeEntityById(entity.id, changeEvent);
    }
    async removeEntityById(entityId, changeEvent = undefined) {
        this.#_entitiesMap.delete(entityId);
        await this.triggerAndAwait('entity-removed', entityId, changeEvent);
    }
    async updateEntity(entityId, newDocument, changeEvent = undefined) {
        const entity = this.getEntity(entityId);
        if (entity) {
            const beforeDoc = entity?._synchronizedDataModel?.toJSON();
            entity._synchronizeFromDocument(newDocument);
            await this.triggerAndAwait('entity-updated', entityId, beforeDoc, entity, changeEvent);
        }
    }
    /*****************************************************/
    /* END MUTATION METHODS */
    /* START FILTERED CHANGE LISTENERS */
    /*****************************************************/
    onSpecificChange(jsonPatchOperationFilterOrMatcherFunction, usersListener, thisContext) {
        const specificChangeListenerId = crypto.randomUUID();
        const specificChangeMatcherCallback = (entityId, beforeUpdateDoc, updatedEntity) => {
            let changeMatches;
            if (TypeUtilities.isOfTypeFunction(jsonPatchOperationFilterOrMatcherFunction)) {
                changeMatches = JsonPatchFilteringUtilities.doesChangeMatchUsingMatcherFunction(beforeUpdateDoc, updatedEntity.toJSON(), jsonPatchOperationFilterOrMatcherFunction);
            }
            else {
                changeMatches = JsonPatchFilteringUtilities.doesChangeMatchOperation(beforeUpdateDoc, updatedEntity.toJSON(), jsonPatchOperationFilterOrMatcherFunction);
            }
            if (changeMatches) {
                usersListener.call(thisContext, entityId, beforeUpdateDoc, updatedEntity);
            }
        };
        this.on('entity-updated', specificChangeMatcherCallback, this);
        this.#_specificChangeListeners.set(specificChangeListenerId, { specificChangeMatcherCallback, usersListener, thisContext });
        return specificChangeListenerId;
    }
    removeSpecificChangeListener(usersListenerOrListenerId) {
        let foundChangeListenerId = usersListenerOrListenerId;
        if (TypeUtilities.isOfTypeFunction(usersListenerOrListenerId)) {
            for (const [changeListenerId, callbackDetails] of this.#_specificChangeListeners) {
                if (callbackDetails.usersListener === usersListenerOrListenerId) {
                    foundChangeListenerId = changeListenerId;
                }
            }
        }
        if (foundChangeListenerId) {
            return this.#_specificChangeListeners.delete(foundChangeListenerId);
        }
    }
    removeAllListeners(eventName) {
        super.removeAllListeners(eventName);
        this.#_specificChangeListeners.clear();
    }
    /*****************************************************/
    /* END FILTERED CHANGE LISTENERS */
    /*****************************************************/
}
export { EntityChangeFeedWatcher };
export default EntityChangeFeedWatcher;